import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "chipZone", "composerInner"]
  static values  = { orgSlug: String }

  connect() {
    this.activeIndex = -1
    this._searchTimer = null
    this._mentionActive = false
    this._handleClickOutside = this._clickOutside.bind(this)
    document.addEventListener("click", this._handleClickOutside)

    // Chip is sticky-until-changed: it persists across sends. Server re-renders
    // the composer after each turn with the chip pre-populated from
    // chat.other_data['active_page_id'], so client-side clearing is unnecessary
    // (and would fight the server render). To detach, user clicks the pill ✕.
    // If a chip was server-rendered, re-measure its width on connect so the
    // textarea's --chip-inset gets set correctly.
    this._recomputeChipInset()
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
    if (this._searchTimer) clearTimeout(this._searchTimer)
  }

  _recomputeChipInset() {
    if (!this.hasChipZoneTarget || !this.hasComposerInnerTarget) return
    const chip = this.chipZoneTarget.querySelector(".laz-page-chip")
    if (!chip) {
      this.composerInnerTarget.style.setProperty("--chip-inset", "0")
      return
    }
    requestAnimationFrame(() => {
      const w = chip.getBoundingClientRect().width
      const inset = w > 0 ? `${Math.round(w + 8)}px` : "0"
      this.composerInnerTarget.style.setProperty("--chip-inset", inset)
    })
  }

  onInput() {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart

    // Find the last @ before cursor that isn't already closed with ]
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")

    if (atIdx === -1 || before.slice(atIdx).includes("]")) {
      this._hide()
      return
    }

    const query = before.slice(atIdx + 1).trim()
    this._mentionActive = true

    if (query.length < 2) {
      this._showEmpty()
      return
    }

    this._search(query)
  }

  onKeydown(event) {
    if (!this._mentionActive) return

    const items = this.dropdownTarget.querySelectorAll("[data-mention-item]")

    if (event.key === "Escape") {
      event.preventDefault()
      this._hide()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1)
      this._highlightItem(items)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this._highlightItem(items)
      return
    }

    // → opens the highlighted report; ← comes back. Mirrors Mini, and matches what a finder-style
    // list trains people to expect.
    if (event.key === "ArrowRight" && this.activeIndex >= 0 && items.length > 0) {
      const el = items[this.activeIndex]
      if (el?.dataset.level === "report" && this._drillInto(el.dataset.storyId)) event.preventDefault()
      return
    }

    if (event.key === "ArrowLeft") {
      if (this._drillOut()) event.preventDefault()
      return
    }

    if (event.key === "Enter" && this.activeIndex >= 0 && items.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      const item = items[this.activeIndex]
      this._selectFromEl(item)
      return
    }
  }

  select(event) {
    this._selectFromEl(event.currentTarget)
  }

  _selectFromEl(el) {
    if (el.dataset.mentionType === "page") {
      this._selectPage(el.dataset.display, el.dataset.pageId)
    } else if (el.dataset.mentionType === "report") {
      this._selectReport(el.dataset)
    } else {
      this._selectItem(el.dataset.display, el.dataset.bqName)
    }
  }

  // A report / page / component pin: its NAME goes into the message text (so the answer reads
  // naturally), and the exact ids go into hidden fields so the pipeline can SCOPE edit/debug to it.
  // If the ids aren't set, the lanes fall back to name inference — so this is purely additive.
  _selectReport(data) {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")
    if (atIdx === -1) return

    const after = val.slice(pos)
    const name = data.name || data.display || ""
    const mention = `@${name}`
    input.value = val.slice(0, atIdx) + mention + " " + after
    input.focus()
    const newPos = atIdx + mention.length + 1
    input.setSelectionRange(newPos, newPos)

    this._setReportRef({ story: data.storyId, page: data.pageId, component: data.componentId, name, level: data.level })
    this._hide()
  }

  _setReportRef({ story, page, component, name, level }) {
    this._setHidden("message[report_story_id]", story)
    this._setHidden("message[report_page_id]", page)
    this._setHidden("message[report_component_id]", component)
    this._setHidden("message[report_ref_name]", name)
    this._setHidden("message[report_ref_level]", level)
  }

  _clearReportRef() {
    this._setReportRef({ story: "", page: "", component: "", name: "", level: "" })
  }

  _setHidden(name, value) {
    const form = this.hasInputTarget ? this.inputTarget.closest("form") : null
    if (!form) return
    let hidden = form.querySelector(`input[name='${name}']`)
    if (!hidden) {
      hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = name
      form.appendChild(hidden)
    }
    hidden.value = value == null ? "" : value
  }

  // Private

  _search(query) {
    if (this._searchTimer) clearTimeout(this._searchTimer)

    this._searchTimer = setTimeout(async () => {
      try {
        // Fire all searches in parallel — reports/pages (small indexes) usually beat entities
        const [reportsResp, pagesResp, entitiesResp] = await Promise.all([
          fetch(`/${this.orgSlugValue}/chats/report_search?q=${encodeURIComponent(query)}`),
          fetch(`/${this.orgSlugValue}/chats/page_search?q=${encodeURIComponent(query)}`),
          fetch(`/${this.orgSlugValue}/entities/search?q=${encodeURIComponent(query)}`)
        ])
        const reports = reportsResp.ok ? await reportsResp.json() : []
        const pages = pagesResp.ok ? await pagesResp.json() : []
        const entities = entitiesResp.ok ? await entitiesResp.json() : []
        this._last = { pages, entities, reports }
        this._drillStoryId = null
        this._renderResults(pages, entities, reports)
      } catch (e) {
        this._hide()
      }
    }, 300)
  }

  // ONE ROW SHAPE FOR EVERY KIND. Reports, pages and records used to render as three
  // different layouts under three group headings; the headings cost a row each to say
  // what a pill on the row says better, and they broke arrow-key navigation into
  // stretches of unselectable rows.
  //
  // Order is deliberate: kind, then name, then the thing that tells two same-named
  // rows apart. Reports here are routinely called "P&L" twice.
  _row(attrs, kind, label, sub, opts = {}) {
    const pairs = Object.entries(attrs)
      .map(([k, v]) => `data-${k}="${this._escapeAttr(String(v ?? ""))}"`).join(" ")
    return `<div data-mention-item ${pairs} data-action="click->chat-mention#select" class="laz-men-row">
      <span class="laz-men-kind">${this._escapeHtml(kind)}</span>
      <span class="laz-men-label">${this._escapeHtml(label)}</span>
      ${opts.draft ? `<span class="laz-men-badge">Draft</span>` : ""}
      ${sub ? `<span class="laz-men-sub">${this._escapeHtml(sub)}</span>` : ""}
      ${opts.more ? `<span class="laz-men-arrow" data-action="click->chat-mention#drillIn">\u203a</span>` : ""}
    </div>`
  }

  // DRILLING IS A FILTER, NOT A FETCH. report_search already returns a matched report's pages and
  // components alongside it — going "into" a report is therefore narrowing what is already on
  // screen, which is why it can be instant and cannot fail.
  _drillInto(storyId) {
    const inside = (this._last?.reports || []).filter(r => String(r.story_id) === String(storyId) && r.level !== "report")
    if (inside.length === 0) return false      // nothing under it — the arrow does nothing rather than emptying the list
    this._drillStoryId = storyId
    this._renderResults([], [], inside)
    return true
  }

  _drillOut() {
    if (!this._drillStoryId) return false
    this._drillStoryId = null
    const l = this._last || { pages: [], entities: [], reports: [] }
    this._renderResults(l.pages, l.entities, l.reports)
    return true
  }

  _renderResults(pages, entities, reports = []) {
    this.activeIndex = -1
    const dd = this.dropdownTarget

    if (pages.length === 0 && entities.length === 0 && reports.length === 0) {
      dd.innerHTML = `<div class="laz-men-empty">No matching reports, pages or records</div>`
      dd.classList.remove("hidden")
      return
    }

    let html = ""

    if (this._drillStoryId) {
      const parent = (this._last?.reports || []).find(r => String(r.story_id) === String(this._drillStoryId) && r.level === "report")
      html += `<div class="laz-men-crumbs">
        <span class="laz-men-crumb" data-action="click->chat-mention#drillOut">‹ all results</span>
        <span class="laz-men-crumb-cur">${this._escapeHtml(parent?.name || "")}</span>
      </div>`
    }

    // A report / page / component to edit or debug. The picked NAME goes into the text;
    // the ids go into hidden fields (see _selectReport) so the pipeline scopes the lane
    // to exactly what was pinned.
    reports.forEach(r => {
      const disp = r.label || r.name || ""
      const kind = r.level === "component" ? "CHART" : (r.level === "page" ? "PAGE" : "STORY")
      // The NAME on the left, its CONTEXT on the right. `label` arrives as a breadcrumb
      // ("Report › Page › Chart"); showing all of it left-aligned pushes every name out of
      // line and truncates the part being searched for. So the leaf is the label and the
      // trail above it is the sub — or the slug, for a report, which has no trail.
      const parts = disp.split(" › ")
      const leaf  = r.name || parts[parts.length - 1] || disp
      const trail = parts.length > 1 ? parts.slice(0, -1).join(" › ") : (r.slug || "")
      html += this._row({
        "mention-type": "report", "display": disp, "name": r.name || disp,
        "level": r.level || "", "story-id": r.story_id || "",
        "page-id": r.page_id || "", "component-id": r.component_id || ""
      }, kind, leaf, trail, { draft: r.status === "draft", more: r.level === "report" })
    })

    // Pages — the anchor context for page-scoped analysis. The report it belongs to is
    // the sub-label, because a page called "Summary" exists in a dozen reports.
    pages.forEach(page => {
      html += this._row(
        { "mention-type": "page", "display": page.page_name, "page-id": page.id },
        "PAGE", page.page_name, page.template_name || ""
      )
    })

    // Records, grouped in the data but flattened here — the table name rides on the row
    // as its sub-label instead of costing a heading.
    entities.forEach(item => {
      html += this._row(
        { "mention-type": "entity", "display": item.display, "bq-name": item.bq_name },
        "TABLE", item.display, item.table_name || ""
      )
    })

    dd.innerHTML = html
    dd.classList.remove("hidden")
  }

  // The chevron is a real control now, not decoration. stopPropagation so opening a report is
  // never mistaken for picking it.
  drillIn(event) {
    event.preventDefault(); event.stopPropagation()
    const row = event.currentTarget.closest("[data-mention-item]")
    if (row) this._drillInto(row.dataset.storyId)
  }

  drillOut(event) {
    event.preventDefault(); event.stopPropagation()
    this._drillOut()
  }

  _showEmpty() {
    const dd = this.dropdownTarget
    dd.innerHTML = `<div class="laz-men-empty">Type to search records…</div>`
    dd.classList.remove("hidden")
  }

  _hide() {
    this._mentionActive = false
    this.activeIndex = -1
    this._drillStoryId = null
    if (this._searchTimer) clearTimeout(this._searchTimer)
    this.dropdownTarget.classList.add("hidden")
  }

  _selectItem(display, bqName) {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")

    if (atIdx === -1) return

    this._clearReportRef() // an entity pick is not a report pin
    const after = val.slice(pos)
    const mention = `@${display} [${bqName}]`
    input.value = val.slice(0, atIdx) + mention + " " + after
    input.focus()

    const newPos = atIdx + mention.length + 1
    input.setSelectionRange(newPos, newPos)

    this._hide()
  }

  // Page selection is a CONTEXT tag, not a table reference — so we:
  //   1. strip the `@query` fragment out of the textarea
  //   2. render a purple pill in the chip zone that floats over the textarea's
  //      leading padding, so the visible question text starts to the right of it
  //   3. write the page id into the hidden form field
  //   4. wire the pill's ✕ button to reverse (2) and (3)
  // Only one chip at a time — selecting a second page swaps the current one.
  _selectPage(display, pageId) {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")
    if (atIdx === -1) return

    this._clearReportRef() // a page-context pick is not a report pin
    // Strip the `@query` fragment; do NOT insert `@display` text
    const after = val.slice(pos)
    input.value = val.slice(0, atIdx) + after
    input.focus()
    input.setSelectionRange(atIdx, atIdx)
    input.dispatchEvent(new Event("input", { bubbles: true }))

    // Set backend hidden field
    this._setHiddenPageId(pageId)

    // Render the chip pill
    this._renderChip(display, pageId)

    this._hide()
  }

  _renderChip(display, pageId) {
    const zone = this.hasChipZoneTarget ? this.chipZoneTarget : null
    if (!zone) return

    zone.innerHTML = ""
    const chip = document.createElement("span")
    chip.className = "laz-page-chip"
    chip.dataset.pageId = pageId
    chip.innerHTML = `
      <svg class="laz-chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
        <polyline points="14 3 14 9 20 9"></polyline>
      </svg>
      <span class="laz-chip-name">${this._escapeHtml(display)}</span>
      <button type="button" class="laz-chip-remove" title="Remove page context" data-action="click->chat-mention#removeChip">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    `
    zone.appendChild(chip)

    // After the chip is laid out, measure its width and push the textarea's
    // text over so it starts to the right of the pill on line 1.
    requestAnimationFrame(() => {
      const w = chip.getBoundingClientRect().width
      const inset = w > 0 ? `${Math.round(w + 8)}px` : "0"
      if (this.hasComposerInnerTarget) {
        this.composerInnerTarget.style.setProperty("--chip-inset", inset)
      }
    })
  }

  removeChip(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this._clearChip()
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  _clearChip() {
    if (this.hasChipZoneTarget) this.chipZoneTarget.innerHTML = ""
    if (this.hasComposerInnerTarget) {
      this.composerInnerTarget.style.setProperty("--chip-inset", "0")
    }
    this._setHiddenPageId("")
  }

  _setHiddenPageId(pageId) {
    const form = this.hasInputTarget ? this.inputTarget.closest("form") : null
    if (!form) return
    let hidden = form.querySelector("input[name='message[mentioned_page_id]']")
    if (!hidden) {
      hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = "message[mentioned_page_id]"
      form.appendChild(hidden)
    }
    hidden.value = pageId
  }

  _highlightItem(items) {
    items.forEach((item, i) => {
      const on = i === this.activeIndex
      item.classList.toggle("is-active", on)
      // The list scrolls at 280px. Without this, arrowing past the fold moves the selection
      // somewhere the user cannot see, and Enter picks a row they never looked at.
      if (on) item.scrollIntoView({ block: "nearest" })
    })
  }

  _clickOutside(event) {
    if (!this._mentionActive) return
    if (this.element.contains(event.target)) return
    this._hide()
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    return str.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
